import React from 'react'
import { createRoot } from 'react-dom/client'
import smoothscroll from 'smoothscroll-polyfill'
import { Provider } from 'react-redux'
import { ErrorBoundary } from '@sentry/react'
import createStore from './store/createStore.js'
import { initSentry, initSentryLifecycleListeners } from './utils/sentry'
import './styles/main.scss'
import '@fortawesome/fontawesome-free/css/all.min.css'
import './utils/object'
import ThemeProvider from './contexts/ThemeProvider'
import DirectionProvider from './contexts/DirectionProvider'
import RR6RouterApp from './navigation/RR6RouterApp'

// ========================================================
// Store Instantiation
// ========================================================
const store = createStore(
  window.___INITIAL_STATE__,
  window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()
)

// ========================================================
// Sentry Setup
// ========================================================
if (__PROD__) {
  initSentry()
  initSentryLifecycleListeners()
}

// ========================================================
// Render Setup (React 18 createRoot API)
// ========================================================
const MOUNT_NODE = document.getElementById('root')
smoothscroll.polyfill()

const root = createRoot(MOUNT_NODE)

const sentryErrorFallback = (
  <div
    style={{
      boxSizing: 'border-box',
      minHeight: '100%',
      padding: '1.5rem',
      fontFamily: 'system-ui, sans-serif',
      textAlign: 'center',
    }}>
    <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem' }}>
      Something went wrong
    </h2>
    <p style={{ margin: 0, color: '#555', maxWidth: '28rem', marginInline: 'auto' }}>
      Please refresh the page. If this keeps happening, contact support.
    </p>
  </div>
)

let render = () => {
  root.render(
    <ErrorBoundary fallback={sentryErrorFallback}>
      <Provider store={store}>
        <DirectionProvider>
          <ThemeProvider>
            <RR6RouterApp store={store} />
          </ThemeProvider>
        </DirectionProvider>
      </Provider>
    </ErrorBoundary>,
  )
}

// This code is excluded from the production bundle
if (__DEV__ && import.meta.hot) {
  const renderApp = render
  const renderError = error => {
    // Keep logging during development-only HMR render failures.
    // eslint-disable-next-line no-console
    console.error(error)
    root.render(
      <div style={{ padding: 20, fontFamily: 'monospace', color: '#c00' }}>
        <h2>Render Error</h2>
        <pre>{error?.message ?? String(error)}</pre>
      </div>
    )
  }

  render = () => {
    try {
      renderApp()
    } catch (error) {
      renderError(error)
    }
  }

  import.meta.hot.accept('./navigation/RR6RouterApp', () => {
    setImmediate(() => {
      render()
    })
  })
}

// ========================================================
// Go!
// ========================================================
render()
